import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getEvent, updateAttendance, claimDriving, releaseDriving } from '../api/client';
import { formatDate, formatTime, getMapsUrl } from '../utils/helpers';
import EventBadge from '../components/EventBadge';
import LoadingSpinner from '../components/LoadingSpinner';
import type { CalendarEvent, AttendanceRecord, DrivingAssignment, AttendanceStatus } from '../types';

export default function EventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, kids } = useAuth();
  const [event, setEvent] = useState<CalendarEvent | null>(null);
  const [attendance, setAttendance] = useState<AttendanceRecord[]>([]);
  const [driving, setDriving] = useState<DrivingAssignment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = async () => {
    if (!id) return;
    try {
      setError(null);
      const data = await getEvent(id);
      setEvent(data.event);
      setAttendance(data.attendance);
      setDriving(data.driving);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, [id]);

  const handleAttendance = async (kidId: string, status: AttendanceStatus) => {
    if (!id) return;
    await updateAttendance(id, kidId, status);
    await refresh();
  };

  const handleClaimDriving = async (type: 'pickup' | 'return') => {
    if (!id) return;
    await claimDriving(id, type);
    await refresh();
  };

  const handleReleaseDriving = async (type: 'pickup' | 'return') => {
    if (!id) return;
    await releaseDriving(id, type);
    await refresh();
  };

  if (loading) return <div className="flex justify-center p-8"><LoadingSpinner /></div>;
  if (error) return <div className="p-4 text-red-500 text-center">{error}</div>;
  if (!event) return <div className="p-4 text-center text-gray-500">Event not found</div>;

  const isDriver = driving?.driverId === user?.id;
  const isReturnDriver = driving?.returnDriverId === user?.id;

  return (
    <div className="max-w-lg mx-auto p-4">
      <button onClick={() => navigate(-1)} className="text-blue-600 dark:text-blue-400 mb-4 flex items-center gap-1">
        &larr; Back
      </button>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5 mb-4">
        <div className="flex items-center gap-2 mb-2">
          <EventBadge type={event.type} status={event.status} />
          <h1 className="text-xl font-bold dark:text-white">{event.title}</h1>
        </div>
        <p className="text-gray-600 dark:text-gray-300">{formatDate(event.date)}</p>
        <p className="text-gray-600 dark:text-gray-300">{formatTime(event.startTime)} – {formatTime(event.endTime)}</p>
        {event.locationName && (
          <a href={getMapsUrl(event.locationName)} target="_blank" rel="noreferrer" className="text-blue-600 dark:text-blue-400 underline text-sm">
            {event.locationName}
          </a>
        )}
        {event.notes && <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{event.notes}</p>}
      </div>

      {kids.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5 mb-4">
          <h2 className="font-semibold mb-3 dark:text-white">Attendance</h2>
          {kids.map(kid => {
            const record = attendance.find(a => a.kidId === kid.id);
            const status = record?.status || 'no_response';
            return (
              <div key={kid.id} className="flex items-center justify-between py-2 border-b last:border-0 dark:border-gray-700">
                <span className="dark:text-gray-200">{kid.name}</span>
                <div className="flex gap-1">
                  {(['going', 'not_going'] as AttendanceStatus[]).map(s => (
                    <button
                      key={s}
                      onClick={() => handleAttendance(kid.id, s)}
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        status === s
                          ? s === 'going' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                          : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                      }`}
                    >
                      {s === 'going' ? 'Going' : 'Not going'}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5">
        <h2 className="font-semibold mb-3 dark:text-white">Driving</h2>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm dark:text-gray-300">Pickup driver</span>
            {isDriver ? (
              <button onClick={() => handleReleaseDriving('pickup')} className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                You (release)
              </button>
            ) : driving?.driverId ? (
              <span className="text-sm text-gray-500">Claimed</span>
            ) : (
              <button onClick={() => handleClaimDriving('pickup')} className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                Claim
              </button>
            )}
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm dark:text-gray-300">Return driver</span>
            {isReturnDriver ? (
              <button onClick={() => handleReleaseDriving('return')} className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                You (release)
              </button>
            ) : driving?.returnDriverId ? (
              <span className="text-sm text-gray-500">Claimed</span>
            ) : (
              <button onClick={() => handleClaimDriving('return')} className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                Claim
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
